// The phone layout: one screen at a time, reached by drilling down a shallow
// tree. The home screen picks the example and runs it, then lists the views —
// debugger, console, lock plan, trace, the graphs, timelines — each of which
// opens full-screen with a back bar to climb out. `App` owns the navigation
// state and renders each screen's body (reusing the very same panel components
// the desktop lays out side by side); this file is just the chrome: the bar, the
// back button, and the tap-through menu lists.

interface MobileNavProps {
  /** The drill-down path. Empty is the home screen; the last entry is the
   *  screen currently on top. */
  path: readonly string[];
  /** Title shown on the home screen's bar. */
  homeTitle: string;
  /** Human title for a screen id, shown on its bar. */
  screenTitle(id: string): string;
  onBack(): void;
  /** The home screen's body — run controls plus the top-level menu. */
  renderHome(): React.ReactNode;
  /** A non-home screen's body. */
  renderScreen(id: string): React.ReactNode;
}

export function MobileNav({
  path,
  homeTitle,
  screenTitle,
  onBack,
  renderHome,
  renderScreen,
}: MobileNavProps) {
  const current = path.length > 0 ? path[path.length - 1]! : null;
  return (
    <div className="m-app">
      <header className="m-bar">
        {current === null ? (
          <span className="m-app-title">{homeTitle}</span>
        ) : (
          <>
            <button className="m-back" onClick={onBack} aria-label="Back">
              <span aria-hidden="true">‹</span> Back
            </button>
            <span className="m-title">{screenTitle(current)}</span>
          </>
        )}
      </header>
      {current === null ? (
        <div className="m-body m-body-scroll">{renderHome()}</div>
      ) : (
        // Keyed by the screen id so switching screens remounts the body — the
        // panels expect to mount fresh into a flex column, and this also drops
        // the previous screen's Monaco / SVG rather than leaving it parked.
        <div className="m-body" key={current}>
          {renderScreen(current)}
        </div>
      )}
    </div>
  );
}

export interface MobileMenuItem {
  id: string;
  title: string;
  /** A one-line description under the title, so the menu explains itself. */
  hint?: string;
}

/** A tap-through list of destinations — the home screen's view list, and the
 *  debugger screen's Locals / Transaction / Threads. */
export function MobileMenu({
  items,
  onSelect,
}: {
  items: readonly MobileMenuItem[];
  onSelect(id: string): void;
}) {
  return (
    <ul className="m-menu">
      {items.map((it) => (
        <li key={it.id}>
          <button className="m-menu-item" onClick={() => onSelect(it.id)}>
            <span className="m-menu-text">
              <span className="m-menu-title">{it.title}</span>
              {it.hint && <span className="m-menu-hint">{it.hint}</span>}
            </span>
            <span className="m-menu-chevron" aria-hidden="true">
              ›
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
